import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import {
  createCentralAccount,
  createOperator,
  createOperatorSubAccount,
  fetchCentralAccounts,
  fetchOperatorUsers,
  fetchOperators,
  resetSubAccountPassword,
  setCentralAccountActive,
  setOperatorSubAccountActive,
  updateOperator
} from '../../redux/slices/operatorsSlice';
import { selectCanManageOperators, changeOwnPassword, logoutUser } from '../../redux/slices/authSlice';
import { fetchSettings } from '../../redux/slices/settingsSlice';

const emptyOperatorForm = {
  id: '',
  name: '',
  legalName: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  vehicleLimit: 1,
  // Quittungs-Stammdaten (§ 33 UStDV) — Pflicht für den Quittungsversand.
  taxNumber: '',
  vatId: '',
  notes: '',
  active: true,
  // Auszahlungs-Referenzdaten für App-Zahlungs-Abrechnung (Konzept 8.7).
  // Steuern KEINE Live-Zahlung — App-Zahlungen gehen immer ans Hauptkonto;
  // die Zentrale rechnet Sub-Unternehmer per Auszahlungsübersicht ab und nutzt
  // diese Angaben, um das Geld zu überweisen.
  payoutMethod: 'none', // 'none' | 'paypal' | 'bank'
  payoutPaypalEmail: '',
  payoutIban: '',
  payoutBic: '',
  payoutAccountHolder: ''
};

const emptySubAccountForm = {
  operatorId: '',
  name: '',
  email: '',
  phone: '',
  password: '',
  role: 'operator_admin'
};

const emptyCentralAccountForm = {
  name: '',
  email: '',
  phone: '',
  password: '',
  role: 'dispatcher'
};

const centralRoleLabel = (role) => {
  switch (role) {
    case 'admin':
    case 'central_admin':
      return 'Admin';
    case 'dispatcher':
    case 'central_dispatch':
      return 'Dispatcher';
    default:
      return role || 'Unbekannt';
  }
};

const OperatorsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canManageOperators = useSelector(selectCanManageOperators);
  const currentUser = useSelector(state => state.auth.user);
  const { operators, usersByOperatorId, centralAccounts, isLoading, isSaving, error } = useSelector(state => state.operators);
  // Instanzweites Fahrzeug-Gesamtlimit (Lizenz) aus appSettings/general.
  const globalVehicleLimit = useSelector(state => state.settings?.global?.vehicleLimit ?? 10);
  // Tatsächlich angelegte Fahrzeuge (app-weit über AppDataManager geladen) —
  // dieselbe Quelle wie die Fahrzeugverwaltung.
  const existingVehicleCount = useSelector(state => state.appData?.vehicles?.length ?? 0);

  const [operatorDialogOpen, setOperatorDialogOpen] = useState(false);
  const [operatorForm, setOperatorForm] = useState(emptyOperatorForm);
  const [subAccountDialogOpen, setSubAccountDialogOpen] = useState(false);
  const [subAccountForm, setSubAccountForm] = useState(emptySubAccountForm);
  const [centralAccountDialogOpen, setCentralAccountDialogOpen] = useState(false);
  const [centralAccountForm, setCentralAccountForm] = useState(emptyCentralAccountForm);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [resetDialogUser, setResetDialogUser] = useState(null);
  const [resetForm, setResetForm] = useState({ next: '', confirm: '' });
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchOperators());
    dispatch(fetchCentralAccounts());
    dispatch(fetchSettings());
  }, [dispatch]);

  useEffect(() => {
    operators.forEach(operator => {
      dispatch(fetchOperatorUsers(operator.id));
    });
  }, [dispatch, operators]);

  const operatorCount = operators.length;
  const activeOperatorCount = useMemo(
    () => operators.filter(operator => operator.active !== false).length,
    [operators]
  );

  // Fahrzeug-Kontingent (Lizenz): Es zählen die TATSÄCHLICH angelegten Fahrzeuge
  // gegen das instanzweite Limit — identisch zur Fahrzeugverwaltung. Das je
  // Unternehmen vergebbare Limit ist durch den noch freien Lizenzrahmen begrenzt.
  const remainingVehicleQuota = Math.max(0, globalVehicleLimit - existingVehicleCount);
  // Beim Bearbeiten das bereits gespeicherte Limit nicht künstlich unterschreiten,
  // damit übrige Felder editierbar bleiben (das eigene Limit bleibt zulässig).
  const editingOperator = operators.find(op => op.id === operatorForm.id);
  const maxVehicleLimitForForm = Math.max(
    remainingVehicleQuota,
    Number(editingOperator?.vehicleLimit) || 0
  );

  const openNewOperatorDialog = () => {
    setOperatorForm(emptyOperatorForm);
    setOperatorDialogOpen(true);
  };

  const openEditOperatorDialog = (operator) => {
    setOperatorForm({
      id: operator.id,
      name: operator.name || '',
      legalName: operator.legalName || '',
      contactName: operator.contactName || '',
      email: operator.email || '',
      phone: operator.phone || '',
      address: operator.address || '',
      vehicleLimit: operator.vehicleLimit ?? 1,
      taxNumber: operator.taxNumber || '',
      vatId: operator.vatId || '',
      notes: operator.notes || '',
      active: operator.active !== false,
      // Auszahlungs-Referenzdaten sind direkt am operators-Dokument gespeichert.
      payoutMethod: operator.payoutMethod || 'none',
      payoutPaypalEmail: operator.payoutPaypalEmail || '',
      payoutIban: operator.payoutIban || '',
      payoutBic: operator.payoutBic || '',
      payoutAccountHolder: operator.payoutAccountHolder || ''
    });
    setOperatorDialogOpen(true);
  };

  const openSubAccountDialog = (operatorId = '') => {
    setSubAccountForm({ ...emptySubAccountForm, operatorId });
    setSubAccountDialogOpen(true);
  };

  const openCentralAccountDialog = () => {
    setCentralAccountForm(emptyCentralAccountForm);
    setCentralAccountDialogOpen(true);
  };

  // Fahrzeuglimit muss eine ganze Zahl im zulässigen Rahmen sein (keine
  // Dezimalwerte, keine leere/ungültige Eingabe).
  const vehicleLimitValue = Number(operatorForm.vehicleLimit);
  const vehicleLimitValid =
    operatorForm.vehicleLimit !== '' &&
    Number.isInteger(vehicleLimitValue) &&
    vehicleLimitValue >= 1 &&
    vehicleLimitValue <= maxVehicleLimitForForm;

  // Pflichtfelder inkl. Quittungs-Stammdaten (§ 33 UStDV: leistender
  // Unternehmer = Name, Anschrift, Steuernummer/USt-IdNr).
  const operatorFormValid =
    operatorForm.name.trim() &&
    operatorForm.legalName.trim() &&
    operatorForm.address.trim() &&
    (operatorForm.taxNumber.trim() || operatorForm.vatId.trim()) &&
    vehicleLimitValid;

  const handleOperatorSubmit = async () => {
    if (!operatorFormValid) return;

    // Fahrzeuglimit hart auf eine Ganzzahl im offenen Kontingent begrenzen.
    const clampedVehicleLimit = Math.min(
      Math.max(1, Math.floor(Number(operatorForm.vehicleLimit)) || 1),
      maxVehicleLimitForForm
    );

    // Auszahlungs-Referenzdaten werden direkt mit dem operators-Dokument
    // gespeichert (Konzept 8.7) — sie steuern keine Live-Zahlung.
    const payload = { ...operatorForm, vehicleLimit: clampedVehicleLimit };
    const action = operatorForm.id
      ? updateOperator(payload)
      : createOperator(payload);
    const result = await dispatch(action);
    if (result.error) return;

    setOperatorDialogOpen(false);
    dispatch(fetchOperators());
  };

  const openPasswordDialog = () => {
    setPasswordForm({ current: '', next: '', confirm: '' });
    setPasswordError('');
    setPasswordDialogOpen(true);
  };

  const handlePasswordSubmit = async () => {
    setPasswordError('');
    if (passwordForm.next.length < 8) {
      setPasswordError('Das neue Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError('Die Passwort-Bestätigung stimmt nicht überein.');
      return;
    }
    setPasswordSaving(true);
    const result = await dispatch(changeOwnPassword({
      currentPassword: passwordForm.current,
      newPassword: passwordForm.next
    }));
    setPasswordSaving(false);
    if (changeOwnPassword.rejected.match(result)) {
      setPasswordError(result.payload || 'Passwort konnte nicht geändert werden.');
      return;
    }
    // Erfolg → automatische Abmeldung und zurück zum Login.
    setPasswordDialogOpen(false);
    await dispatch(logoutUser());
    navigate('/login', { replace: true });
  };

  const openResetDialog = (user) => {
    setResetDialogUser(user);
    setResetForm({ next: '', confirm: '' });
    setResetError('');
    setResetSuccess(false);
    setResetDialogOpen(true);
  };
  // Steuert das Öffnen/Schließen über resetDialogUser (kein separater Bool nötig).
  const setResetDialogOpen = (open) => {
    if (!open) setResetDialogUser(null);
  };

  const handleResetSubmit = async () => {
    setResetError('');
    if (resetForm.next.length < 8) {
      setResetError('Das neue Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (resetForm.next !== resetForm.confirm) {
      setResetError('Die Passwort-Bestätigung stimmt nicht überein.');
      return;
    }
    setResetSaving(true);
    const result = await dispatch(resetSubAccountPassword({
      userId: resetDialogUser.id,
      newPassword: resetForm.next
    }));
    setResetSaving(false);
    if (resetSubAccountPassword.rejected.match(result)) {
      setResetError(result.payload || 'Passwort konnte nicht zurückgesetzt werden.');
      return;
    }
    setResetSuccess(true);
  };

  const handleSubAccountSubmit = async () => {
    if (!subAccountForm.operatorId || !subAccountForm.name.trim() || !subAccountForm.email.trim() || !subAccountForm.password) {
      return;
    }

    const result = await dispatch(createOperatorSubAccount(subAccountForm));
    if (!result.error) {
      setSubAccountDialogOpen(false);
      dispatch(fetchOperators());
    }
  };

  const handleCentralAccountSubmit = async () => {
    if (!centralAccountForm.name.trim() || !centralAccountForm.email.trim() || !centralAccountForm.password) {
      return;
    }

    const result = await dispatch(createCentralAccount(centralAccountForm));
    if (!result.error) {
      setCentralAccountDialogOpen(false);
    }
  };

  const toggleCentralAccountActive = (user) => {
    dispatch(setCentralAccountActive({
      userId: user.id,
      active: user.active === false
    }));
  };

  const toggleSubAccountActive = (operatorId, user) => {
    dispatch(setOperatorSubAccountActive({
      userId: user.id,
      operatorId,
      active: user.active === false
    }));
  };

  if (!canManageOperators) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Sie haben keine Berechtigung für die Unternehmens- und Sub-Konten-Verwaltung.
        </Alert>
      </Box>
    );
  }

  if (isLoading && operators.length === 0) {
    return <Loading message="Unternehmer werden geladen..." />;
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Unternehmen & Sub-Konten"
        description="Unternehmen, Kontingente und Portal-Zugänge verwalten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Profil', path: '/profil' },
          { label: 'Unternehmen', path: '/profil/unternehmer' }
        ]}
        actionButton={{
          label: 'Unternehmen anlegen',
          icon: <AddIcon />,
          onClick: openNewOperatorDialog
        }}
        secondaryButtons={[
          {
            label: 'Zentral-Konto anlegen',
            icon: <AdminIcon />,
            onClick: openCentralAccountDialog
          },
          {
            label: 'Sub-Konto anlegen',
            icon: <PersonAddIcon />,
            onClick: () => openSubAccountDialog()
          },
          {
            label: 'Mein Passwort ändern',
            icon: <LockResetIcon />,
            onClick: openPasswordDialog
          }
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip label={`${activeOperatorCount}/${operatorCount} Unternehmen aktiv`} color="primary" />
        <Chip
          label={`Fahrzeuge: ${existingVehicleCount}/${globalVehicleLimit} angelegt`}
          variant="outlined"
          color={existingVehicleCount >= globalVehicleLimit ? 'warning' : 'default'}
        />
        <Chip label="Sub-Konten haben nur Portalzugriff" variant="outlined" />
      </Stack>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Hauptzentrale
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Kontakt</TableCell>
                <TableCell>Rolle</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {centralAccounts.map(user => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{user.name || user.email}</Typography>
                    <Typography variant="caption" color="text.secondary">{user.id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.email || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{user.phone || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={centralRoleLabel(user.role)}
                      color={user.role === 'admin' || user.role === 'central_admin' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={user.active === false ? 'Inaktiv' : 'Aktiv'}
                      color={user.active === false ? 'default' : 'success'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      disabled={user.active !== false && user.id === currentUser?.uid}
                      onClick={() => toggleCentralAccountActive(user)}
                    >
                      {user.active === false ? 'Aktivieren' : 'Deaktivieren'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {centralAccounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">Noch keine Hauptzentralen-Konten geladen.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Unternehmen
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Unternehmen</TableCell>
              <TableCell>Kontakt</TableCell>
              <TableCell align="center">Fahrzeuglimit</TableCell>
              <TableCell>Sub-Konten</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {operators.map(operator => {
              const users = usersByOperatorId[operator.id] || [];
              return (
                <TableRow key={operator.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{operator.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {operator.legalName || operator.id}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        size="small"
                        label={operator.active === false ? 'Inaktiv' : 'Aktiv'}
                        color={operator.active === false ? 'default' : 'success'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{operator.contactName || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {operator.email || operator.phone || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{operator.vehicleLimit ?? 0}</TableCell>
                  <TableCell>
                    {users.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Keine Sub-Konten</Typography>
                    ) : (
                      <Stack spacing={0.75}>
                        {users.map(user => (
                          <Stack key={user.id} direction="row" spacing={1} alignItems="center">
                            <Chip
                              size="small"
                              label={user.active === false ? 'Inaktiv' : 'Aktiv'}
                              color={user.active === false ? 'default' : 'success'}
                            />
                            <Typography variant="body2">{user.name || user.email}</Typography>
                            <Button size="small" onClick={() => toggleSubAccountActive(operator.id, user)}>
                              {user.active === false ? 'Aktivieren' : 'Deaktivieren'}
                            </Button>
                            <Button
                              size="small"
                              startIcon={<LockResetIcon />}
                              onClick={() => openResetDialog(user)}
                            >
                              Passwort
                            </Button>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" startIcon={<EditIcon />} onClick={() => openEditOperatorDialog(operator)}>
                        Bearbeiten
                      </Button>
                      <Button size="small" startIcon={<PersonAddIcon />} onClick={() => openSubAccountDialog(operator.id)}>
                        Sub-Konto
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {operators.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">Noch keine Unternehmen angelegt.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={operatorDialogOpen} onClose={() => setOperatorDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{operatorForm.id ? 'Unternehmen bearbeiten' : 'Unternehmen anlegen'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Stammdaten
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Anzeigename"
                  value={operatorForm.name}
                  onChange={(e) => setOperatorForm({ ...operatorForm, name: e.target.value })}
                  helperText="Dieser Name wird in Listen, Zuordnungen und Sub-Konten angezeigt."
                />
                <TextField
                  fullWidth
                  required
                  label="Rechtlicher Name"
                  value={operatorForm.legalName}
                  onChange={(e) => setOperatorForm({ ...operatorForm, legalName: e.target.value })}
                  placeholder="Vollständige Firmierung, z. B. Muster Taxi GmbH"
                  helperText="Erscheint als leistender Unternehmer auf den Quittungen (§ 33 UStDV)."
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Kontakt
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Ansprechpartner"
                  value={operatorForm.contactName}
                  onChange={(e) => setOperatorForm({ ...operatorForm, contactName: e.target.value })}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="email"
                      label="E-Mail"
                      value={operatorForm.email}
                      onChange={(e) => setOperatorForm({ ...operatorForm, email: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Telefon"
                      value={operatorForm.phone}
                      onChange={(e) => setOperatorForm({ ...operatorForm, phone: e.target.value })}
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  required
                  label="Adresse"
                  value={operatorForm.address}
                  onChange={(e) => setOperatorForm({ ...operatorForm, address: e.target.value })}
                  placeholder="Straße Hausnr., PLZ Ort"
                  helperText="Anschrift des Unternehmens — Pflicht für Quittungen."
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Steuerliche Angaben (für Quittungen)
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                Diese Angaben erscheinen auf den automatisch erstellten Quittungen
                der Fahrten dieses Unternehmens. Mindestens eines der beiden Felder
                ist Pflicht. Eine vorläufige Steuernummer kann später geändert werden;
                neue Quittungen verwenden dann automatisch die aktuelle Nummer.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Steuernummer"
                    value={operatorForm.taxNumber}
                    onChange={(e) => setOperatorForm({ ...operatorForm, taxNumber: e.target.value })}
                    placeholder="z. B. 12/345/67890"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="USt-IdNr (optional)"
                    value={operatorForm.vatId}
                    onChange={(e) => setOperatorForm({ ...operatorForm, vatId: e.target.value })}
                    placeholder="z. B. DE123456789"
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Kontingent
              </Typography>
              <TextField
                fullWidth
                required
                type="number"
                label="Fahrzeuglimit"
                value={operatorForm.vehicleLimit}
                onChange={(e) => setOperatorForm({ ...operatorForm, vehicleLimit: e.target.value })}
                inputProps={{ min: 1, max: maxVehicleLimitForForm, step: 1 }}
                error={!vehicleLimitValid}
                helperText={
                  maxVehicleLimitForForm <= 0
                    ? `Kein freier Lizenzrahmen mehr: ${existingVehicleCount} von ${globalVehicleLimit} Fahrzeugen bereits angelegt.`
                    : !vehicleLimitValid
                      ? `Bitte eine ganze Zahl zwischen 1 und ${maxVehicleLimitForForm} eingeben.`
                      : `Maximal anlegbare Fahrzeuge dieses Unternehmens. Frei im Lizenzrahmen: ${remainingVehicleQuota} (${existingVehicleCount}/${globalVehicleLimit} bereits angelegt).`
                }
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Auszahlung (App-Zahlungen)
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                App-Zahlungen laufen immer über das Hauptkonto der Zentrale.
                Diese Angaben sind optional und dienen nur der Abrechnung: Sie
                hinterlegen, wie der Unternehmer seinen Anteil aus der
                Auszahlungsübersicht erhalten möchte.
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Auszahlungsart</InputLabel>
                <Select
                  label="Auszahlungsart"
                  value={operatorForm.payoutMethod}
                  onChange={(e) => setOperatorForm({ ...operatorForm, payoutMethod: e.target.value })}
                >
                  <MenuItem value="none">Keine Angabe</MenuItem>
                  <MenuItem value="paypal">PayPal</MenuItem>
                  <MenuItem value="bank">Banküberweisung</MenuItem>
                </Select>
              </FormControl>

              {operatorForm.payoutMethod === 'paypal' && (
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label="PayPal-E-Mail-Adresse"
                  placeholder="zahlungen@sub-unternehmen.de"
                  value={operatorForm.payoutPaypalEmail}
                  onChange={(e) => setOperatorForm({ ...operatorForm, payoutPaypalEmail: e.target.value })}
                />
              )}

              {operatorForm.payoutMethod === 'bank' && (
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Kontoinhaber"
                    value={operatorForm.payoutAccountHolder}
                    onChange={(e) => setOperatorForm({ ...operatorForm, payoutAccountHolder: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="IBAN"
                    value={operatorForm.payoutIban}
                    onChange={(e) => setOperatorForm({ ...operatorForm, payoutIban: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="BIC (optional)"
                    value={operatorForm.payoutBic}
                    onChange={(e) => setOperatorForm({ ...operatorForm, payoutBic: e.target.value })}
                  />
                </Stack>
              )}
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notizen"
              value={operatorForm.notes}
              onChange={(e) => setOperatorForm({ ...operatorForm, notes: e.target.value })}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={operatorForm.active}
                  onChange={(e) => setOperatorForm({ ...operatorForm, active: e.target.checked })}
                />
              }
              label="Unternehmen ist aktiv"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOperatorDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" disabled={isSaving || !operatorFormValid} onClick={handleOperatorSubmit}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mein Passwort ändern</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Ändert das Passwort Ihres aktuell angemeldeten Kontos
              ({currentUser?.email || 'unbekannt'}). Nach dem Ändern werden Sie
              automatisch abgemeldet und müssen sich neu anmelden.
            </Alert>
            {passwordError && <Alert severity="error">{passwordError}</Alert>}
            <TextField
              type="password"
              label="Aktuelles Passwort"
              value={passwordForm.current}
              onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
              autoComplete="current-password"
            />
            <TextField
              type="password"
              label="Neues Passwort"
              value={passwordForm.next}
              onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })}
              helperText="Mindestens 8 Zeichen."
              autoComplete="new-password"
            />
            <TextField
              type="password"
              label="Neues Passwort bestätigen"
              value={passwordForm.confirm}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
              autoComplete="new-password"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={passwordSaving || !passwordForm.current || passwordForm.next.length < 8 || passwordForm.next !== passwordForm.confirm}
            onClick={handlePasswordSubmit}
          >
            Ändern & abmelden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!resetDialogUser} onClose={() => setResetDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Passwort zurücksetzen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {resetSuccess ? (
              <Alert severity="success">
                Das Passwort für {resetDialogUser?.name || resetDialogUser?.email} wurde
                gesetzt. Bitte teilen Sie es dem Zugang sicher mit — der Nutzer muss sich
                anschließend neu anmelden.
              </Alert>
            ) : (
              <>
                <Alert severity="info">
                  Setzt das Portal-Passwort für <b>{resetDialogUser?.name || resetDialogUser?.email}</b>.
                  Das Passwort wird nicht gespeichert und muss dem Zugang sicher mitgeteilt werden.
                </Alert>
                {resetError && <Alert severity="error">{resetError}</Alert>}
                <TextField
                  type="password"
                  label="Neues Passwort"
                  value={resetForm.next}
                  onChange={(e) => setResetForm({ ...resetForm, next: e.target.value })}
                  helperText="Mindestens 8 Zeichen."
                  autoComplete="new-password"
                />
                <TextField
                  type="password"
                  label="Neues Passwort bestätigen"
                  value={resetForm.confirm}
                  onChange={(e) => setResetForm({ ...resetForm, confirm: e.target.value })}
                  autoComplete="new-password"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>{resetSuccess ? 'Schließen' : 'Abbrechen'}</Button>
          {!resetSuccess && (
            <Button
              variant="contained"
              disabled={resetSaving || resetForm.next.length < 8 || resetForm.next !== resetForm.confirm}
              onClick={handleResetSubmit}
            >
              Passwort setzen
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={centralAccountDialogOpen} onClose={() => setCentralAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Hauptzentralen-Konto anlegen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField required label="Name" value={centralAccountForm.name} onChange={(e) => setCentralAccountForm({ ...centralAccountForm, name: e.target.value })} />
            <TextField required label="E-Mail" value={centralAccountForm.email} onChange={(e) => setCentralAccountForm({ ...centralAccountForm, email: e.target.value })} />
            <TextField label="Telefon" value={centralAccountForm.phone} onChange={(e) => setCentralAccountForm({ ...centralAccountForm, phone: e.target.value })} />
            <TextField required type="password" label="Initiales Passwort" value={centralAccountForm.password} onChange={(e) => setCentralAccountForm({ ...centralAccountForm, password: e.target.value })} helperText="Mindestens 8 Zeichen. Passwort später per Firebase Auth zurücksetzen lassen." />
            <FormControl fullWidth>
              <InputLabel id="central-role-label">Rolle</InputLabel>
              <Select
                labelId="central-role-label"
                label="Rolle"
                value={centralAccountForm.role}
                onChange={(e) => setCentralAccountForm({ ...centralAccountForm, role: e.target.value })}
              >
                <MenuItem value="dispatcher">Dispatcher</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="info">
              Dispatcher duerfen operativ disponieren. Admins erhalten Vollzugriff fuer die Hauptzentrale.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCentralAccountDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={isSaving || !centralAccountForm.name || !centralAccountForm.email || centralAccountForm.password.length < 8}
            onClick={handleCentralAccountSubmit}
          >
            Anlegen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={subAccountDialogOpen} onClose={() => setSubAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sub-Konto anlegen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel id="operator-label">Unternehmer</InputLabel>
              <Select
                labelId="operator-label"
                label="Unternehmer"
                value={subAccountForm.operatorId}
                onChange={(e) => setSubAccountForm({ ...subAccountForm, operatorId: e.target.value })}
              >
                {operators.map(operator => (
                  <MenuItem key={operator.id} value={operator.id}>{operator.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField required label="Name" value={subAccountForm.name} onChange={(e) => setSubAccountForm({ ...subAccountForm, name: e.target.value })} />
            <TextField required label="E-Mail" value={subAccountForm.email} onChange={(e) => setSubAccountForm({ ...subAccountForm, email: e.target.value })} />
            <TextField label="Telefon" value={subAccountForm.phone} onChange={(e) => setSubAccountForm({ ...subAccountForm, phone: e.target.value })} />
            <TextField required type="password" label="Initiales Passwort" value={subAccountForm.password} onChange={(e) => setSubAccountForm({ ...subAccountForm, password: e.target.value })} helperText="Mindestens 8 Zeichen. Passwort später per Firebase Auth zurücksetzen lassen." />
            <FormControl fullWidth>
              <InputLabel id="role-label">Rolle</InputLabel>
              <Select
                labelId="role-label"
                label="Rolle"
                value={subAccountForm.role}
                onChange={(e) => setSubAccountForm({ ...subAccountForm, role: e.target.value })}
              >
                <MenuItem value="operator_admin">Unternehmer-Admin</MenuItem>
                <MenuItem value="operator_staff">Unternehmer-Mitarbeiter</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubAccountDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={isSaving || !subAccountForm.operatorId || !subAccountForm.name || !subAccountForm.email || subAccountForm.password.length < 8}
            onClick={handleSubAccountSubmit}
          >
            Anlegen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperatorsPage;
